import { SidebarMenu, SidebarMenuItem, appStrings as commonAppStrings } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { logoutThunk } from '@backrs_ui/grow-ui/src/redux/session/logoutThunk';
import { UI_PERMISSIONS } from '@backrs_ui/grow-ui/src/redux/user/user.transformer';
import { exitEmulation, readReturnToken } from '@backrs_ui/grow-ui/src/utils/emulation';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

import { SectionDependencies } from './menuSections.hook';

const PROFILE_POPUP_TITLES = appStrings.modules.backrsPageShell.menuItems.profile;

const getAccountTopSectionItems = (
  navigate: SectionDependencies['navigate'],
  userId: number | undefined,
  isUnauth: boolean,
  userPermissions: SectionDependencies['userPermissions'],
  onOpenAssigneeEmulationPalette?: SectionDependencies['onOpenAssigneeEmulationPalette'],
  isPaywalled = false,
): SidebarMenuItem[] => {
  const items: SidebarMenuItem[] = [
    {
      id: 'sidebar-view-profile',
      leftIcon: {
        iconName: 'User01',
      },
      title: PROFILE_POPUP_TITLES.viewProfile,
      variant: {
        type: 'NAVIGATION_ITEM',
        onClick: () => navigate(`${APP_ROUTE.PROFILE}/${userId}`),
        isActive: false,
      },
    },
    {
      id: 'sidebar-account-settings',
      leftIcon: {
        iconName: 'Settings01',
      },
      title: PROFILE_POPUP_TITLES.accountSettings,
      variant: {
        type: 'NAVIGATION_ITEM',
        onClick: () => navigate(APP_ROUTE.SETTINGS),
        isActive: false,
      },
    },
    {
      id: 'sidebar-my-story',
      leftIcon: {
        iconName: 'BookOpen01',
      },
      title: PROFILE_POPUP_TITLES.myStory,
      variant: {
        type: 'NAVIGATION_ITEM',
        onClick: () => navigate(`${APP_ROUTE.PROFILE}?tab=Story`),
        isActive: false,
      },
    },
  ];

  // Add conditional emulation menu item
  if (userPermissions?.[UI_PERMISSIONS.EMULATE_USERS]?.allowed && !IS_SANDBOX) {
    items.push({
      id: 'sidebar-emulate-assignees',
      leftIcon: {
        iconName: 'Users01',
      },
      title: commonAppStrings.userMenu.emulateAssignees,
      variant: {
        type: 'NAVIGATION_ITEM',
        onClick: () => {
          onOpenAssigneeEmulationPalette?.();
        },
        isActive: false,
      },
    });
  }

  if (isUnauth) {
    return items.map((item) => ({
      ...item,
      hidden: true,
    }));
  }

  if (isPaywalled) {
    return [];
  }

  return items;
};

const accountBottomSectionItems = ({
  dispatch,
  postHog,
  navigate,
  isEmulating,
}: Pick<
  SectionDependencies,
  'dispatch' | 'postHog' | 'navigate' | 'isEmulating'
>): SidebarMenuItem[] => {
  const returnToken = isEmulating ? readReturnToken() : null;

  if (returnToken) {
    return [
      {
        id: 'sidebar-exit-emulation',
        leftIcon: {
          iconName: 'LogOut01',
        },
        title: commonAppStrings.userMenu.exitEmulation,
        variant: {
          type: 'NAVIGATION_ITEM',
          onClick: () => {
            postHog.reset();
            exitEmulation(returnToken);
          },
          isActive: false,
        },
      },
    ];
  }

  return [
    {
      id: 'sidebar-sign-out',
      leftIcon: {
        iconName: 'LogOut01',
      },
      title: PROFILE_POPUP_TITLES.signOut,
      variant: {
        type: 'NAVIGATION_ITEM',
        onClick: () => {
          navigate(APP_ROUTE.LOGIN);
          dispatch(logoutThunk({ postHog }));
        },
        isActive: false,
      },
    },
  ];
};

const unauthAccountBottomSectionItems = ({
  navigate,
}: Pick<SectionDependencies, 'navigate'>): SidebarMenuItem[] => {
  return [
    {
      id: 'sidebar-log-in',
      leftIcon: {
        iconName: 'LogIn01',
      },
      title: PROFILE_POPUP_TITLES.logIn,
      variant: {
        type: 'NAVIGATION_ITEM',
        onClick: () => {
          navigate(APP_ROUTE.LOGIN);
        },
        isActive: false,
      },
    },
  ];
};

export const popupSectionMenuItems = ({
  dispatch,
  postHog,
  navigate,
  isUnauthenticated,
  userId,
  userPermissions,
  onOpenAssigneeEmulationPalette,
  isPaywalled,
  isEmulating,
}: SectionDependencies): SidebarMenu['accountMenuItems'] => {
  return {
    topSectionItems: getAccountTopSectionItems(
      navigate,
      userId,
      isUnauthenticated,
      userPermissions,
      onOpenAssigneeEmulationPalette,
      isPaywalled,
    ),
    // Signing out of the sandbox clears the minted token and lands on a login page it doesn't serve.
    bottomSectionItems: IS_SANDBOX
      ? []
      : isUnauthenticated
        ? unauthAccountBottomSectionItems({ navigate })
        : accountBottomSectionItems({ dispatch, postHog, navigate, isEmulating }),
  };
};
